import React, { useState, useEffect, useRef, useMemo } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   SIMPTOM — Landing Page
   Aesthetic: Dark cinematic editorial. A surgical instrument, not a pamphlet.
   ═══════════════════════════════════════════════════════════════════════════ */

const C = {
  bg:        '#0B0F1A',
  bgCard:    '#141928',
  bgElevated:'#1A2035',
  blue:      '#0E33FF',
  blueGlow:  'rgba(14,51,255,0.15)',
  green:     '#5EE89C',
  greenDim:  'rgba(94,232,156,0.08)',
  greenGlow: 'rgba(94,232,156,0.2)',
  text:      '#E8ECF4',
  textMuted: '#7A869A',
  textDim:   '#4A5568',
  border:    'rgba(255,255,255,0.06)',
  borderHover: 'rgba(255,255,255,0.12)',
};

const FONT = {
  display: "'Outfit', sans-serif",
  body: "'DM Sans', sans-serif",
};

const keyframes = `
@keyframes lp-fadeUp {
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lp-glow {
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
}
@keyframes lp-cursor {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
}
@keyframes lp-scanline {
  0%   { top: 0; }
  100% { top: 100%; }
}
@keyframes lp-grain {
  0%, 100% { transform: translate(0, 0); }
  10%  { transform: translate(-2%, -2%); }
  30%  { transform: translate(1%, 3%); }
  50%  { transform: translate(-3%, 1%); }
  70%  { transform: translate(3%, -1%); }
  90%  { transform: translate(-1%, 2%); }
}
@keyframes lp-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
}
@keyframes lp-barGrow {
  from { width: 0; }
}
`;

/* ── Intersection Observer hook ─────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Typing simulation ──────────────────────────────────────────────────── */
function useTyping(text, speed = 45, startDelay = 800) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDisplayed('');
    setDone(false);
    const timeout = setTimeout(() => {
      let i = 0;
      const iv = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) { clearInterval(iv); setDone(true); }
      }, speed);
      return () => clearInterval(iv);
    }, startDelay);
    return () => clearTimeout(timeout);
  }, [text, speed, startDelay]);
  return [displayed, done];
}

/* ════════════════════════════════════════════════════════════════════════════ */
export default function LandingPage({ onNavigateToLogin, onNavigateToRegister }) {
  return (
    <>
      <style>{keyframes}</style>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Outfit:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <div style={{
        fontFamily: FONT.body,
        color: C.text,
        background: C.bg,
        overflowX: 'hidden',
        minHeight: '100vh',
        position: 'relative',
      }}>
        {/* Grain overlay */}
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
          animation: 'lp-grain 4s steps(8) infinite',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Navbar onLogin={onNavigateToLogin} />
          <Hero onStart={onNavigateToRegister} onLogin={onNavigateToLogin} />
          <Features />
          <TrustBar />
          <FooterCTA onStart={onNavigateToRegister} />
          <Footer />
        </div>
      </div>
    </>
  );
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
function Navbar({ onLogin }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      padding: '0 clamp(20px, 4vw, 48px)',
      height: 72,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: scrolled ? 'rgba(11,15,26,0.85)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px) saturate(1.4)' : 'none',
      borderBottom: scrolled ? `1px solid ${C.border}` : '1px solid transparent',
      transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: `linear-gradient(135deg, ${C.blue}, #4F6AFF)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 20px ${C.blueGlow}`,
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L21.5 7v10L12 22 2.5 17V7L12 2z" />
            <path d="M9 9h6M12 6v12" />
          </svg>
        </div>
        <span style={{
          fontFamily: FONT.display, fontWeight: 800, fontSize: 19,
          letterSpacing: '-0.03em', color: C.text,
        }}>
          SIMPTOM
        </span>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Login */}
        <button onClick={onLogin} style={{
          padding: '9px 22px', borderRadius: 8,
          border: `1px solid ${C.border}`,
          background: 'transparent', color: C.text,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: FONT.body, transition: 'all 0.25s ease',
          letterSpacing: '-0.01em',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.background = C.blueGlow; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'transparent'; }}
        >
          Sign in
        </button>
      </div>
    </nav>
  );
}

/* ── Hero ────────────────────────────────────────────────────────────────── */
function Hero({ onStart, onLogin }) {
  const transcriptLines = useMemo(() => [
    'Patient presents with chest pain for 3 days,',
    'radiating to the left arm, worsened by exertion...',
  ], []);

  const fullText = transcriptLines.join(' ');
  const [typed] = useTyping(fullText, 38, 1200);

  return (
    <section style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: 'clamp(100px, 14vh, 140px) clamp(20px, 4vw, 48px) clamp(60px, 10vh, 100px)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
        width: '140vw', height: '80vh',
        background: `radial-gradient(ellipse at center, ${C.blueGlow} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', right: '-5%',
        width: '60vw', height: '40vh',
        background: `radial-gradient(ellipse at center, ${C.greenDim} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />

      <div style={{
        maxWidth: 1280, margin: '0 auto', width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
        gap: 'clamp(40px, 6vw, 80px)', alignItems: 'center',
        position: 'relative', zIndex: 1,
      }}>
        {/* Left — Copy */}
        <div style={{ animation: 'lp-fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) both' }}>
          {/* Eyebrow */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', borderRadius: 100,
            background: C.greenDim,
            border: `1px solid rgba(94,232,156,0.12)`,
            marginBottom: 28,
            animation: 'lp-fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both',
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%', background: C.green,
              animation: 'lp-glow 2.5s ease-in-out infinite',
            }} />
            <span style={{
              fontSize: 12, fontWeight: 600, color: C.green,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              fontFamily: FONT.body,
            }}>
              Medical AI
            </span>
          </div>

          <h1 style={{
            fontFamily: FONT.display, fontWeight: 900,
            fontSize: 'clamp(2.8rem, 6vw, 4.8rem)',
            lineHeight: 1.1, letterSpacing: '-0.04em',
            margin: 0, color: C.text,
          }}>
            You speak.
            <br />
            <span style={{
              background: `linear-gradient(135deg, ${C.blue} 20%, ${C.green} 80%)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>SIMPTOM</span>
            <span style={{ color: C.textMuted }}> writes.</span>
          </h1>

          <p style={{
            fontSize: 'clamp(1rem, 1.5vw, 1.15rem)',
            lineHeight: 1.7, color: C.textMuted, marginTop: 24, maxWidth: 480,
            animation: 'lp-fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both',
          }}>
            Real-time medical transcription, AI-powered clinical data extraction, and automatic report generation — so you can focus on your patient.
          </p>

          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 40,
            animation: 'lp-fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both',
          }}>
            <button onClick={onStart} style={{
              padding: '15px 34px', borderRadius: 10, border: 'none',
              background: C.blue, color: '#fff',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
              fontFamily: FONT.display, letterSpacing: '-0.01em',
              transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              boxShadow: `0 0 30px ${C.blueGlow}, 0 4px 16px rgba(0,0,0,0.3)`,
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = `0 0 50px rgba(14,51,255,0.3), 0 8px 24px rgba(0,0,0,0.4)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 0 30px ${C.blueGlow}, 0 4px 16px rgba(0,0,0,0.3)`;
              }}
            >
              Get started free
              <i className="fa-solid fa-arrow-right" style={{ marginLeft: 12, fontSize: 13 }} />
            </button>

            <button onClick={onLogin} style={{
              padding: '15px 28px', borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: 'rgba(255,255,255,0.03)', color: C.textMuted,
              fontSize: 15, fontWeight: 500, cursor: 'pointer',
              fontFamily: FONT.body, transition: 'all 0.25s ease',
            }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
            >
              Sign in
            </button>
          </div>
        </div>

        {/* Right — Live transcription demo */}
        <div style={{
          animation: 'lp-fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both',
          display: 'flex', justifyContent: 'center',
        }}>
          <div style={{
            width: '100%', maxWidth: 480,
            background: C.bgCard, borderRadius: 16,
            border: `1px solid ${C.border}`,
            overflow: 'hidden',
            boxShadow: `0 0 60px rgba(0,0,0,0.3), 0 0 30px ${C.blueGlow}`,
          }}>
            {/* Terminal header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', gap: 7 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#FF5F57' }} />
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#FFBD2E' }} />
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28C840' }} />
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 11, fontWeight: 500, color: C.textDim,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                <div style={{
                  width: 6, height: 6, borderRadius: '50%', background: C.green,
                  animation: 'lp-glow 2s ease-in-out infinite',
                }} />
                LIVE
              </div>
            </div>

            {/* Transcription area */}
            <div style={{ padding: '20px 22px', minHeight: 120 }}>
              <div style={{
                fontSize: 11, fontWeight: 600, color: C.blue,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                marginBottom: 12, fontFamily: FONT.body,
              }}>
                <i className="fa-solid fa-microphone-lines" style={{ marginRight: 8, fontSize: 10 }} />
                Live transcription
              </div>
              <p style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13, lineHeight: 1.8, color: C.text,
                margin: 0, minHeight: 48,
              }}>
                {typed}
                <span style={{
                  display: 'inline-block', width: 2, height: 16,
                  background: C.green, marginLeft: 2, verticalAlign: 'text-bottom',
                  animation: 'lp-cursor 1s step-end infinite',
                }} />
              </p>
            </div>

            {/* Divider with extraction label */}
            <div style={{
              padding: '10px 22px',
              background: 'rgba(14,51,255,0.04)',
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <i className="fa-solid fa-brain" style={{ fontSize: 10, color: C.blue }} />
              <span style={{
                fontSize: 10, fontWeight: 600, color: C.blue,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                fontFamily: FONT.body,
              }}>
                AI extraction
              </span>
              <div style={{
                marginLeft: 'auto', display: 'flex', gap: 2, alignItems: 'center',
              }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{
                    width: 3, height: 3, borderRadius: '50%',
                    background: C.blue, opacity: 0.6,
                    animation: `lp-glow 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>

            {/* Structured output */}
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Motif', width: '72%', delay: '0.8s' },
                { label: 'Location', width: '58%', delay: '1.2s' },
                { label: 'Severity', width: '40%', delay: '1.6s' },
              ].map((row) => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: C.textDim,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    width: 72, flexShrink: 0,
                    fontFamily: FONT.body,
                  }}>{row.label}</span>
                  <div style={{
                    height: 5, borderRadius: 3, flex: 1, overflow: 'hidden',
                    background: 'rgba(255,255,255,0.04)',
                  }}>
                    <div style={{
                      height: '100%', width: row.width, borderRadius: 3,
                      background: `linear-gradient(90deg, ${C.blue}, rgba(14,51,255,0.4))`,
                      animation: `lp-barGrow 1.2s cubic-bezier(0.22, 1, 0.36, 1) ${row.delay} both`,
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Features ────────────────────────────────────────────────────────────── */
function Features() {
  const [ref, visible] = useInView(0.08);

  const cards = [
    { icon: 'fa-microphone-lines', title: 'Real-time transcription', desc: 'Speak naturally during the consultation. SIMPTOM captures every word with medical-grade speech recognition AI.', num: '01' },
    { icon: 'fa-brain', title: 'AI clinical extraction', desc: 'Clinical data is automatically structured into 10 professional medical sections: chief complaint, history, vitals, prescriptions…', num: '02' },
    { icon: 'fa-file-medical', title: 'Structured reports', desc: 'Generate complete medical reports in one click. PDF export, multilingual translation, and DMP submission built in.', num: '03' },
  ];

  return (
    <section ref={ref} style={{
      padding: 'clamp(80px, 10vh, 120px) clamp(20px, 4vw, 48px)',
      maxWidth: 1280, margin: '0 auto',
    }}>
      <div style={{
        marginBottom: 72,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        <h2 style={{
          fontFamily: FONT.display, fontWeight: 800,
          fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
          letterSpacing: '-0.03em', color: C.text, margin: 0,
        }}>
          Why SIMPTOM?
        </h2>
        <div style={{
          width: 56, height: 3, borderRadius: 2, marginTop: 20,
          background: `linear-gradient(90deg, ${C.blue}, ${C.green})`,
        }} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 24,
      }}>
        {cards.map((card, i) => (
          <FeatureCard key={card.num} {...card} delay={i * 0.1} visible={visible} />
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ icon, title, desc, num, delay, visible }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: 36, borderRadius: 16,
        background: hovered ? C.bgElevated : C.bgCard,
        border: `1px solid ${hovered ? C.borderHover : C.border}`,
        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        transitionDelay: `${delay}s`,
        opacity: visible ? 1 : 0,
        transform: visible
          ? hovered ? 'translateY(-6px)' : 'translateY(0)'
          : 'translateY(30px)',
        cursor: 'default',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Glow on hover */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: hovered
          ? `linear-gradient(90deg, transparent, ${C.blue}, transparent)`
          : 'transparent',
        transition: 'background 0.4s ease',
      }} />

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24,
      }}>
        <div style={{
          width: 50, height: 50, borderRadius: 14,
          background: `rgba(14,51,255,0.08)`,
          border: `1px solid rgba(14,51,255,0.12)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease',
          transform: hovered ? 'scale(1.08)' : 'scale(1)',
        }}>
          <i className={`fa-solid ${icon}`} style={{ fontSize: 20, color: C.blue }} />
        </div>
        <span style={{
          fontFamily: FONT.display, fontWeight: 800, fontSize: 32,
          color: 'rgba(255,255,255,0.04)', letterSpacing: '-0.04em',
        }}>{num}</span>
      </div>

      <h3 style={{
        fontFamily: FONT.display, fontWeight: 700, fontSize: 18,
        letterSpacing: '-0.02em', color: C.text, margin: '0 0 10px',
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: 14, lineHeight: 1.75, color: C.textMuted, margin: 0,
      }}>
        {desc}
      </p>
    </div>
  );
}

/* ── Trust Bar ───────────────────────────────────────────────────────────── */
function TrustBar() {
  const [ref, visible] = useInView(0.2);

  const badges = [
    { icon: 'fa-shield-halved', label: 'HDS-certified hosting' },
    { icon: 'fa-circle-check', label: 'Ségur du Numérique compliant' },
    { icon: 'fa-lock', label: 'GDPR' },
  ];

  return (
    <section ref={ref} style={{
      padding: '44px clamp(20px, 4vw, 48px)',
      borderTop: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{
        maxWidth: 900, margin: '0 auto',
        display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 48,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        {badges.map((b) => (
          <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className={`fa-solid ${b.icon}`} style={{ fontSize: 16, color: C.textDim }} />
            <span style={{
              fontSize: 13, fontWeight: 600, color: C.textDim,
              letterSpacing: '0.02em', textTransform: 'uppercase',
              fontFamily: FONT.body,
            }}>
              {b.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Footer CTA ──────────────────────────────────────────────────────────── */
function FooterCTA({ onStart }) {
  const [ref, visible] = useInView(0.15);

  return (
    <section ref={ref} style={{
      padding: 'clamp(80px, 10vh, 120px) clamp(20px, 4vw, 48px)',
      textAlign: 'center', position: 'relative', overflow: 'hidden',
    }}>
      {/* Glow */}
      <div style={{
        position: 'absolute', bottom: '-20%', left: '50%', transform: 'translateX(-50%)',
        width: '100vw', height: '60vh',
        background: `radial-gradient(ellipse at center, ${C.blueGlow} 0%, transparent 55%)`,
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative', zIndex: 1, maxWidth: 560, margin: '0 auto',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        <h2 style={{
          fontFamily: FONT.display, fontWeight: 800,
          fontSize: 'clamp(1.6rem, 3.5vw, 2.6rem)',
          letterSpacing: '-0.03em', color: C.text,
          margin: 0, lineHeight: 1.15, textAlign: 'center',
        }}>
          Ready to save time on your medical reports?
        </h2>
        <p style={{ fontSize: 15, color: C.textMuted, marginTop: 14, lineHeight: 1.6, textAlign: 'center' }}>
          Join the physicians who trust SIMPTOM.
        </p>
        <button onClick={onStart} style={{
          marginTop: 36, padding: '16px 40px', borderRadius: 10,
          border: 'none', background: C.blue, color: '#fff',
          fontSize: 15, fontWeight: 700, cursor: 'pointer',
          fontFamily: FONT.display, letterSpacing: '-0.01em',
          transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: `0 0 40px ${C.blueGlow}, 0 4px 20px rgba(0,0,0,0.3)`,
        }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 0 60px rgba(14,51,255,0.3), 0 8px 30px rgba(0,0,0,0.4)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `0 0 40px ${C.blueGlow}, 0 4px 20px rgba(0,0,0,0.3)`;
          }}
        >
          Get started free
          <i className="fa-solid fa-arrow-right" style={{ marginLeft: 12, fontSize: 13 }} />
        </button>
      </div>
    </section>
  );
}

/* ── Footer ──────────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={{
      padding: '28px clamp(20px, 4vw, 48px)',
      borderTop: `1px solid ${C.border}`,
      textAlign: 'center',
    }}>
      <span style={{ fontSize: 12, color: C.textDim, fontFamily: FONT.body }}>
        © 2026 SIMPTOM. All rights reserved.
      </span>
    </footer>
  );
}
